import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import moment from "moment";
import {
  Search, X, Plus, Download, Phone, MessageCircle, Users, Sparkles, AlarmClock, TrendingUp,
  ChevronLeft, ChevronRight, ArrowUpDown, CalendarPlus, Mail, Megaphone, Inbox, Loader2,
} from "lucide-react";
import SegmentedControl from "../../components/SegmentedControl";
import FilterMultiSelect from "../../components/FilterMultiSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { downloadXlsx } from "../../lib/xlsx";
import { cn } from "../../lib/utils";
import { getSocket } from "../../lib/socket";
import { getLeads, getLeadSummary, getLead, updateLead, addLeadNote, createLead } from "../../api/api";
import {
  LEAD_SOURCES, SOURCE_LABEL, SOURCE_STYLE, SOURCE_DETAIL_LABEL, LEAD_STATUS_LABEL, STATE_LABEL, LEAD_CHAT_EVENT,
} from "../../lib/lead-constants";

// Every lead MediFlick has — ads, Google Business Profile, website form,
// WhatsApp, front desk — with where it came from. The conversation itself
// lives in the Lead Inbox; this is the list staff work through.

const PAGE_SIZE = 25;
const OPEN = ["new", "contacted", "follow_up"];
const STATUSES = Object.keys(LEAD_STATUS_LABEL);
const STATUS_VARIANT = {
  new: "info", contacted: "secondary", follow_up: "warning", booked: "default", lost: "outline", junk: "destructive",
};
const LOST_REASONS = ["Not responding", "Price too high", "Treatment not available", "Booked elsewhere", "Only enquiring", "Wrong number"];

const ENQUIRY_PRESETS = [
  { value: "any", label: "Any time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7d" },
  { value: "30d", label: "Last 30d" },
];
const enquiryFrom = (preset) =>
  ({ today: moment(), "7d": moment().subtract(6, "days"), "30d": moment().subtract(29, "days") })[preset]?.format("YYYY-MM-DD");

const FOLLOW_UP_OPTIONS = [
  { value: "overdue", label: "Overdue" },
  { value: "today", label: "Due today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "week", label: "Next 7 days" },
  { value: "none", label: "No follow-up set" },
];

const GOOGLE_DETAILS = ["gbp_call", "gbp_whatsapp", "gbp_website", "gbp_booking", "google_ads_form", "google_ads_click"];

const selectClass = "h-9 rounded-md border border-input bg-card px-2.5 text-sm";
const errMsg = (err, fallback) => err?.message || fallback;
const sourceText = (lead) =>
  `${SOURCE_LABEL[lead.source] ?? lead.source}${lead.source_detail && SOURCE_DETAIL_LABEL[lead.source_detail] ? ` · ${SOURCE_DETAIL_LABEL[lead.source_detail]}` : ""}`;

function StatusBadge({ status }) {
  if (status === "converted") {
    return <Badge className="bg-green-600 text-white hover:bg-green-600">{LEAD_STATUS_LABEL[status]}</Badge>;
  }
  return <Badge variant={STATUS_VARIANT[status]}>{LEAD_STATUS_LABEL[status] ?? status}</Badge>;
}

function SourceMark({ source, size = "sm" }) {
  const s = SOURCE_STYLE[source] ?? SOURCE_STYLE.other;
  return (
    <span
      title={SOURCE_LABEL[source]}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-md font-bold text-white",
        size === "sm" ? "h-5 min-w-5 px-1 text-[10px]" : "h-8 min-w-8 px-1.5 text-xs"
      )}
      style={{ backgroundColor: s.color }}
    >
      {s.short}
    </span>
  );
}

// follow_up_on is a date (YYYY-MM-DD), not a time.
const followUpState = (lead) => {
  if (!lead.follow_up_on || !OPEN.includes(lead.status)) return null;
  const f = moment(lead.follow_up_on);
  if (f.isBefore(moment(), "day")) return "overdue";
  if (f.isSame(moment(), "day")) return "today";
  return "upcoming";
};

function FollowUpCell({ lead }) {
  const state = followUpState(lead);
  if (!state) return <span className="text-muted-foreground">—</span>;
  const f = moment(lead.follow_up_on);
  return (
    <span
      className={cn(
        "whitespace-nowrap text-sm",
        state === "overdue" && "font-medium text-destructive",
        state === "today" && "font-medium text-warning"
      )}
    >
      {state === "today" ? "Today" : f.isSame(moment().add(1, "day"), "day") ? "Tomorrow" : f.format("ddd, D MMM")}
      {state === "overdue" && <span className="block text-xs font-normal">overdue</span>}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, hint, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        <Icon className={cn("h-4 w-4", tone || "text-muted-foreground")} />
      </div>
      <p className="mt-2 font-serif text-3xl font-semibold tabular-nums">{value ?? "—"}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const whatsappUrl = (lead, clinic) =>
  `https://wa.me/91${lead.phone}?text=${encodeURIComponent(
    `Hello${lead.name ? ` ${lead.name.split(" ")[0]}` : ""}, thank you for your enquiry${lead.service_interest ? ` about ${lead.service_interest}` : ""} at ${clinic || "our clinic"}. When would be a good time to call you?`
  )}`;

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{value}</span>
    </div>
  );
}

// ------------------------------------------------------------- attribution

const ATTRIBUTION_ROWS = [
  ["utm_source", "UTM source"],
  ["utm_medium", "UTM medium"],
  ["utm_campaign", "UTM campaign"],
  ["utm_term", "UTM term"],
  ["utm_content", "UTM content"],
  ["ad_campaign_id", "Ads campaign ID"],
  ["ad_group_id", "Ads ad group ID"],
  ["ad_creative_id", "Ads ad ID"],
  ["ad_form_id", "Ads form ID"],
  ["gclid", "Google click ID"],
  ["landing_page", "Landing page"],
  ["referrer", "Referrer"],
];

function AttributionPanel({ lead }) {
  const rows = ATTRIBUTION_ROWS.filter(([k]) => lead.attribution?.[k]);
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <h4 className="mb-2 text-sm font-semibold">Where this lead came from</h4>
      <dl className="space-y-1 text-sm">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Channel</dt>
          <dd className="text-right">{sourceText(lead)}</dd>
        </div>
        {lead.campaign && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Campaign</dt>
            <dd className="text-right">{lead.campaign}</dd>
          </div>
        )}
        {rows.map(([k, label]) => (
          <div key={k} className="flex justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">{label}</dt>
            <dd className="min-w-0 break-all text-right font-mono text-xs leading-5">{lead.attribution[k]}</dd>
          </div>
        ))}
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">First contact</dt>
          <dd className="text-right">{moment(lead.createdAt).format("D MMM YYYY, h:mm A")}</dd>
        </div>
      </dl>
    </div>
  );
}

// ------------------------------------------------------------------ drawer

function LeadDrawer({ leadId, clinicName, onClose, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [lostReason, setLostReason] = useState(LOST_REASONS[0]);
  const [closing, setClosing] = useState(null); // "lost" | "junk"
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => getLead(leadId).then((res) => setDetail(res.data)).catch((err) => toast.error(errMsg(err, "Could not load the lead."))),
    [leadId]
  );
  useEffect(() => { load(); }, [load]);

  const save = async (fn, success) => {
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
      onChanged();
    } catch (err) {
      toast.error(errMsg(err, "Something went wrong."));
    } finally {
      setBusy(false);
    }
  };

  const lead = detail?.lead;

  const setStatus = (status) => {
    if (status === lead.status) return;
    if (status === "lost" || status === "junk") return setClosing(status);
    save(() => updateLead(lead._id, { status }));
  };

  const bookAppointment = () => {
    navigator.clipboard?.writeText(lead.phone).catch(() => {});
    toast.success(`${lead.phone} copied — paste it on OPD Booking.`);
    navigate("/opd");
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto bg-card shadow-xl">
        {!lead ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <>
            <div className="border-b border-border p-5">
              <button onClick={onClose} className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100" aria-label="Close">
                <X className="h-4 w-4" />
              </button>
              <div className="flex items-center gap-3 pr-6">
                <SourceMark source={lead.source} size="lg" />
                <div className="min-w-0">
                  <h3 className="truncate font-serif text-lg font-semibold">{lead.name || `+91 ${lead.phone}`}</h3>
                  <p className="text-sm text-muted-foreground">{sourceText(lead)}</p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Button size="sm" variant="outline" onClick={() => { window.location.href = `tel:${lead.phone}`; }}>
                  <Phone className="h-3.5 w-3.5" /> Call
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(whatsappUrl(lead, clinicName), "_blank")}>
                  <MessageCircle className="h-3.5 w-3.5 text-green-600" /> WhatsApp
                </Button>
                <Button size="sm" variant="outline" disabled={!detail.conversation_id} onClick={() => navigate(`/lead-inbox?c=${detail.conversation_id}`)}>
                  <Inbox className="h-3.5 w-3.5" /> Chat
                </Button>
                <Button size="sm" onClick={bookAppointment}>
                  <CalendarPlus className="h-3.5 w-3.5" /> Book
                </Button>
              </div>
            </div>

            <div className="space-y-5 p-5">
              <div className="space-y-2 text-sm">
                <Row icon={Phone} label="Phone" value={`+91 ${lead.phone}`} />
                {lead.email && <Row icon={Mail} label="Email" value={lead.email} />}
                {lead.service_interest && <Row icon={Sparkles} label="Interested in" value={lead.service_interest} />}
                {lead.campaign && <Row icon={Megaphone} label="Campaign" value={lead.campaign} />}
                {detail.conversation_state && <Row icon={Inbox} label="Assistant" value={STATE_LABEL[detail.conversation_state]} />}
                {lead.note && <Row icon={AlarmClock} label="Enquiry" value={<span className="whitespace-pre-line">{lead.note}</span>} />}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <select className={cn(selectClass, "w-full")} value={lead.status} disabled={busy} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => <option key={s} value={s}>{LEAD_STATUS_LABEL[s]}</option>)}
                  </select>
                </div>
                {OPEN.includes(lead.status) && (
                  <div className="space-y-1.5">
                    <Label>Next follow-up</Label>
                    <Input
                      type="date"
                      value={lead.follow_up_on || ""}
                      disabled={busy}
                      onChange={(e) => save(() => updateLead(lead._id, { follow_up_on: e.target.value || null }))}
                    />
                  </div>
                )}
                {lead.lost_reason && ["lost", "junk"].includes(lead.status) && (
                  <p className="col-span-2 text-sm text-muted-foreground">Reason: {lead.lost_reason}</p>
                )}
              </div>

              {closing && (
                <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
                  <Label>Why is this lead {closing}?</Label>
                  <select className={cn(selectClass, "w-full")} value={lostReason} onChange={(e) => setLostReason(e.target.value)}>
                    {LOST_REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => setClosing(null)}>Cancel</Button>
                    <Button
                      size="sm"
                      disabled={busy}
                      onClick={() => save(() => updateLead(lead._id, { status: closing, lost_reason: lostReason })).then(() => setClosing(null))}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              )}

              <AttributionPanel lead={lead} />

              <div className="space-y-2">
                <Label>Add a note</Label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  placeholder="e.g. Spoke to patient, wants an evening slot"
                  className="w-full rounded-md border border-input bg-background p-2 text-sm"
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!note.trim() || busy}
                    onClick={() => save(() => addLeadNote(lead._id, note.trim())).then(() => setNote(""))}
                  >
                    Save note
                  </Button>
                </div>
              </div>

              <div>
                <h4 className="mb-3 text-sm font-semibold">Activity</h4>
                {detail.activity.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing yet.</p>
                ) : (
                  <ol className="relative space-y-4 border-l border-border pl-4">
                    {detail.activity.map((a) => (
                      <li key={a._id} className="relative">
                        <span className="absolute -left-[21px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-primary" />
                        <p className="text-sm">{a.text}</p>
                        <p className="text-xs text-muted-foreground">{moment(a.createdAt).format("D MMM, h:mm A")}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- add lead

const EMPTY_LEAD = { name: "", phone: "", source: "phone", service_interest: "", note: "" };

function AddLeadModal({ open, onOpenChange, onAdded, services }) {
  const [form, setForm] = useState(EMPTY_LEAD);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) return toast.error("Enter a valid 10-digit mobile number.");
    setSaving(true);
    try {
      const res = await createLead(form);
      toast.success(res.message || "Lead added.");
      setForm(EMPTY_LEAD);
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error(errMsg(err, "Could not add the lead."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} maxLength={120} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile *</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="9876543210" required />
            </div>
            <div className="space-y-1.5">
              <Label>Source</Label>
              <select className={cn(selectClass, "h-10 w-full")} value={form.source} onChange={(e) => set("source", e.target.value)}>
                {LEAD_SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Interested in</Label>
              <Input list="lead-services" value={form.service_interest} onChange={(e) => set("service_interest", e.target.value)} maxLength={200} />
              <datalist id="lead-services">
                {services.map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Input value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="What did they ask about?" maxLength={2000} />
          </div>
          <p className="text-xs text-muted-foreground">The assistant will message this number on WhatsApp straight away.</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>Add lead</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------- page

export default function Leads() {
  const clinicName = useSelector((s) => s.settings.data?.clinic?.name);
  const [list, setList] = useState({ data: [], total: 0, status_counts: {}, numberOfPage: 1 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [sources, setSources] = useState([]);
  const [details, setDetails] = useState([]);
  const [services, setServices] = useState([]);
  const [enquiry, setEnquiry] = useState("any");
  const [followUp, setFollowUp] = useState([]);
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);

  const filterParams = useCallback(
    () => ({
      search: search.trim() || undefined,
      source: sources.join(",") || undefined,
      source_detail: details.join(",") || undefined,
      service: services.join(",") || undefined,
      from: enquiryFrom(enquiry),
      follow_up: followUp.join(",") || undefined,
    }),
    [search, sources, details, services, enquiry, followUp]
  );

  const loadList = useCallback(
    () =>
      getLeads({ ...filterParams(), status: status === "all" ? undefined : status, sort: sort.key, dir: sort.dir, page, limit: PAGE_SIZE })
        .then((res) => setList(res))
        .catch((err) => toast.error(errMsg(err, "Could not load leads.")))
        .finally(() => setLoading(false)),
    [filterParams, status, sort, page]
  );
  const loadSummary = useCallback(() => getLeadSummary().then((res) => setSummary(res.data)).catch(() => {}), []);
  const reload = useCallback(() => Promise.all([loadList(), loadSummary()]), [loadList, loadSummary]);

  useEffect(() => { const t = setTimeout(loadList, 250); return () => clearTimeout(t); }, [loadList]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // New leads arrive from webhooks at any time — refresh on any lead chat
  // event (a new lead always opens one), with a slow poll as a backstop.
  useEffect(() => {
    const socket = getSocket();
    let t;
    const onChat = () => { clearTimeout(t); t = setTimeout(reload, 500); };
    socket?.on(LEAD_CHAT_EVENT, onChat);
    const poll = setInterval(reload, 60000);
    return () => { socket?.off(LEAD_CHAT_EVENT, onChat); clearInterval(poll); clearTimeout(t); };
  }, [reload]);

  const hasFilters = search || sources.length || details.length || services.length || enquiry !== "any" || followUp.length;
  const clearFilters = () => {
    setSearch(""); setSources([]); setDetails([]); setServices([]); setEnquiry("any"); setFollowUp([]); setPage(1);
  };
  const withReset = (setter) => (v) => { setter(v); setPage(1); };
  const toggleIn = (arr, setter, key) => withReset(setter)(arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key]);

  const toggleSort = (key) => {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "follow_up_on" ? "asc" : "desc" }));
    setPage(1);
  };

  const exportLeads = async () => {
    try {
      const res = await getLeads({ ...filterParams(), status: status === "all" ? undefined : status, sort: sort.key, dir: sort.dir, page: 1, limit: 5000 });
      downloadXlsx(
        `leads_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
        ["Name", "Phone", "Email", "Source", "Campaign", "Interested in", "Status", "Enquiry", "Next follow-up", "UTM source", "UTM medium", "UTM campaign", "Google click ID", "Note"],
        res.data.map((l) => [
          l.name, l.phone, l.email, sourceText(l), l.campaign, l.service_interest, LEAD_STATUS_LABEL[l.status],
          moment(l.createdAt).format("D MMM YYYY h:mm A"), l.follow_up_on ? moment(l.follow_up_on).format("D MMM YYYY") : "",
          l.attribution?.utm_source ?? "", l.attribution?.utm_medium ?? "", l.attribution?.utm_campaign ?? "", l.attribution?.gclid ?? "", l.note,
        ]),
        { sheetName: "Leads", widths: [22, 13, 26, 30, 26, 20, 18, 18, 14, 16, 14, 20, 24, 40] }
      );
      toast.success(`Exported ${res.data.length} lead${res.data.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(errMsg(err, "Could not export leads."));
    }
  };

  const SortHead = ({ k, children }) => (
    <button type="button" onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground">
      {children}
      <ArrowUpDown className={cn("h-3 w-3", sort.key === k ? "opacity-100" : "opacity-40")} />
    </button>
  );

  const counts = list.status_counts || {};
  const allCount = Object.values(counts).reduce((a, b) => a + b, 0);
  const rate = summary?.total_30d ? Math.round((summary.won_30d / summary.total_30d) * 100) : 0;
  const pages = list.numberOfPage || 1;
  const showGoogleDetails = sources.includes("google") || details.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Leads</h2>
          <p className="text-sm text-muted-foreground">Every enquiry from ads, Google, the website, WhatsApp and the front desk — in one place</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportLeads}>
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> Add lead
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={Users} label="Total leads" value={summary?.total} hint={summary && `${summary.open} still open`} />
        <StatCard icon={Sparkles} label="New today" value={summary?.new_today} hint="Enquiries received today" tone="text-info" />
        <StatCard
          icon={AlarmClock}
          label="Follow-ups due"
          value={summary?.due}
          hint={summary && (summary.overdue ? `${summary.overdue} overdue` : "None overdue")}
          tone={summary?.overdue ? "text-destructive" : undefined}
        />
        <StatCard icon={TrendingUp} label="Booked / converted (30d)" value={summary?.won_30d} hint={summary && `${rate}% conversion`} tone="text-green-600" />
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {LEAD_SOURCES.filter((s) => summary?.by_source?.[s.key] || sources.includes(s.key)).map((s) => {
            const active = sources.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => toggleIn(sources, setSources, s.key)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors",
                  active ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:bg-muted/50"
                )}
              >
                <SourceMark source={s.key} />
                {s.label}
                <span className="tabular-nums text-muted-foreground">{summary?.by_source?.[s.key] ?? 0}</span>
              </button>
            );
          })}
        </div>
        {showGoogleDetails && (
          <div className="flex flex-wrap items-center gap-2 pl-1">
            <span className="text-xs text-muted-foreground">Google via</span>
            {GOOGLE_DETAILS.map((key) => {
              const active = details.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleIn(details, setDetails, key)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-xs transition-colors",
                    active ? "border-primary bg-primary/5 font-medium" : "border-border bg-card hover:bg-muted/50"
                  )}
                >
                  {SOURCE_DETAIL_LABEL[key]} <span className="tabular-nums text-muted-foreground">{summary?.by_source_detail?.[key] ?? 0}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => withReset(setSearch)(e.target.value)}
              placeholder="Search name, phone, campaign or note…"
              className="h-9 pl-9"
            />
          </div>
          <FilterMultiSelect label="Source" options={LEAD_SOURCES.map((s) => ({ value: s.key, label: s.label }))} value={sources} onChange={withReset(setSources)} />
          {summary?.services?.length > 0 && (
            <FilterMultiSelect label="Interested in" options={summary.services.map((s) => ({ value: s, label: s }))} value={services} onChange={withReset(setServices)} />
          )}
          <FilterMultiSelect label="Follow-up" options={FOLLOW_UP_OPTIONS} value={followUp} onChange={withReset(setFollowUp)} />
          <SegmentedControl size="sm" items={ENQUIRY_PRESETS} value={enquiry} onChange={withReset(setEnquiry)} />
          {hasFilters ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
          ) : null}
        </div>
        <SegmentedControl
          items={[
            { value: "all", label: "All", count: allCount },
            ...STATUSES.map((s) => ({ value: s, label: LEAD_STATUS_LABEL[s], count: counts[s] ?? 0 })),
          ]}
          value={status}
          onChange={withReset(setStatus)}
        />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Lead</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Interested in</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium"><SortHead k="createdAt">Enquiry</SortHead></th>
                <th className="px-4 py-3 font-medium"><SortHead k="follow_up_on">Next follow-up</SortHead></th>
                <th className="px-4 py-3 font-medium">Assistant</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {list.data.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-muted-foreground">
                    {loading ? (
                      <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    ) : hasFilters || status !== "all" ? (
                      <>
                        No leads match these filters.
                        <Button variant="link" onClick={() => { clearFilters(); setStatus("all"); }}>Clear filters</Button>
                      </>
                    ) : (
                      "No leads yet. They'll appear here as soon as someone enquires."
                    )}
                  </td>
                </tr>
              ) : (
                list.data.map((l) => (
                  <tr key={l._id} onClick={() => setOpenId(l._id)} className="cursor-pointer border-t border-border hover:bg-muted/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <SourceMark source={l.source} />
                        <div className="min-w-0">
                          <p className="truncate font-medium">{l.name || "—"}</p>
                          <p className="text-xs text-muted-foreground">{l.phone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="max-w-[220px] px-4 py-3">
                      <p className="truncate">{SOURCE_LABEL[l.source] ?? l.source}</p>
                      {(SOURCE_DETAIL_LABEL[l.source_detail] || l.campaign) && (
                        <p className="truncate text-xs text-muted-foreground">{SOURCE_DETAIL_LABEL[l.source_detail] || l.campaign}</p>
                      )}
                    </td>
                    <td className="max-w-[180px] truncate px-4 py-3">{l.service_interest || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground" title={moment(l.createdAt).format("D MMM YYYY, h:mm A")}>
                      {moment(l.createdAt).fromNow()}
                    </td>
                    <td className="px-4 py-3"><FollowUpCell lead={l} /></td>
                    <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{STATE_LABEL[l.conversation_state] ?? "—"}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`Call ${l.name || l.phone}`} onClick={() => { window.location.href = `tel:${l.phone}`; }}>
                          <Phone className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={`WhatsApp ${l.name || l.phone}`} onClick={() => window.open(whatsappUrl(l, clinicName), "_blank")}>
                          <MessageCircle className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3 text-sm text-muted-foreground">
          <span>
            {list.total === 0 ? "0" : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, list.total)}`} of {list.total} leads
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span>Page {page} of {pages}</span>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {openId && <LeadDrawer key={openId} leadId={openId} clinicName={clinicName} onClose={() => setOpenId(null)} onChanged={reload} />}
      <AddLeadModal open={adding} onOpenChange={setAdding} onAdded={reload} services={summary?.services ?? []} />
    </div>
  );
}
